import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Modal,
  Text,
  Icon,
  IconButton,
  Link,
  Alert,
} from '@nimbus-ds/components';
import { CheckCircleIcon, PlusCircleIcon, TrashIcon } from '@nimbus-ds/icons';
import { CurrencyType } from '@tiendanube/common';
import { CancelAndConfirmButtons } from 'commons/components';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { DiscountRow } from './DiscountRow';
import { InputPercentageWithError } from './InputPercentageWithError';
import { InputWithError } from './InputWithError';
import { OrderEditDiscount } from '../../Products';
import {
  OrderEditDiscountType,
  PROMOTIONAL_DISCOUNT,
} from '../../Products/types';

interface DiscountEditModalProps {
  show: boolean;
  closeModal: () => void;
  discounts: OrderEditDiscount[];
  updateDiscounts: (discounts: OrderEditDiscount[]) => void;
  newTotalWithoutDiscounts: number;
  currency: CurrencyType;
  total: number;
}

export function DiscountEditModal({
  show,
  closeModal,
  discounts,
  updateDiscounts,
  newTotalWithoutDiscounts,
  currency,
  total,
}: Readonly<DiscountEditModalProps>) {
  const t = useTranslationOrders();
  const [couponReplaced, setCouponReplaced] = useState(false);
  const [editedDiscounts, setEditedDiscounts] = useState(discounts);

  const editDiscount = useMemo(
    () => discounts.find((discount) => discount.id === 'editCouponDiscount'),
    [discounts],
  );

  const hasOnlyEditDiscounts = useMemo(
    () => editDiscount !== undefined && discounts.length === 1,
    [editDiscount, discounts],
  );

  const initialDiscountValue = useMemo(
    () => editDiscount?.value,
    [editDiscount],
  );

  const [discount, setDiscount] = useState<string>(
    initialDiscountValue?.toString() ?? '',
  );

  const [hasError, setHasError] = useState(false);

  const parseNumber = useCallback(
    (value: string | number | undefined): number => {
      if (value === undefined) return NaN;
      if (typeof value === 'number')
        return Number.isFinite(value) ? value : NaN;
      const normalizedValue = value.trim().replace(',', '.');
      const parsed = Number(normalizedValue);
      return Number.isFinite(parsed) ? parsed : NaN;
    },
    [],
  );

  const discountNumber = useMemo(() => {
    if (discount === '') return undefined;
    const parsed = parseNumber(discount);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, [discount, parseNumber]);

  const [showEditDiscountInput, setShowEditDiscountInput] = useState(
    discounts.length === 0,
  );
  const [discountPercentage, setDiscountPercentage] = useState<string>('');

  const calculatePercentageOfDiscount = useCallback(
    (
      discount: string | number | undefined,
      total: string | number | undefined,
    ): string => {
      const discountNum = parseNumber(discount);
      const totalNum = parseNumber(total);

      // Fallback to empty string if any value is invalid or <= 0
      if (
        Number.isNaN(discountNum) ||
        Number.isNaN(totalNum) ||
        discountNum <= 0 ||
        totalNum <= 0
      ) {
        return '';
      }

      const percentage = (discountNum / totalNum) * 100;

      // Additional fallback for NaN result
      if (Number.isNaN(percentage)) return '';

      return percentage.toFixed(0);
    },
    [parseNumber],
  );

  useEffect(() => {
    setEditedDiscounts(discounts);
    setDiscountPercentage(
      calculatePercentageOfDiscount(
        discounts.find((d) => d.id === 'editCouponDiscount')?.value ?? 0,
        total,
      ),
    );
    setShowEditDiscountInput(
      discounts.length === 0 ||
        hasOnlyEditDiscounts ||
        editDiscount !== undefined,
    );
  }, [
    discounts,
    hasOnlyEditDiscounts,
    editDiscount,
    calculatePercentageOfDiscount,
    total,
  ]);

  const totalDiscount = useMemo(
    () =>
      editedDiscounts
        .filter((d) => d.editType !== 'remove')
        .reduce((acc, discount) => acc + discount.value, 0),
    [editedDiscounts],
  );

  const existingCouponDiscount = useMemo(
    () => discounts.find((d) => d.id === 'couponDiscount'),
    [discounts],
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newDiscountValue = e.target.value;
    const newDiscountValueTrimmed = newDiscountValue.trim();
    const newDiscountValueNumber = parseNumber(newDiscountValueTrimmed);
    const isValidNumber = Number.isFinite(newDiscountValueNumber);

    setHasError(
      isValidNumber &&
        (newDiscountValueNumber <= 0 || newDiscountValueNumber > maxDiscount),
    );
    setDiscount(newDiscountValueTrimmed);
    // calculate percentage of discount
    if (isValidNumber) {
      setDiscountPercentage(
        calculatePercentageOfDiscount(newDiscountValueNumber, total),
      );
    } else {
      setDiscountPercentage('');
    }
  };

  // Handle change of percentage input. Recalculate discount value based on total. Show percentage value.
  const handlePercentageInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newDiscountValuePercentage = e.target.value;
    const valueTrimmed = newDiscountValuePercentage.trim();
    const newDiscountPercentage = parseNumber(valueTrimmed);
    const isValidPercentage = Number.isFinite(newDiscountPercentage);
    const calculatedDiscount = isValidPercentage
      ? (newDiscountPercentage / 100) * total
      : NaN;
    const isCalculatedFinite = Number.isFinite(calculatedDiscount);
    setHasError(
      isValidPercentage &&
        (newDiscountPercentage <= 0 ||
          newDiscountPercentage > 100 ||
          (isCalculatedFinite && calculatedDiscount > maxDiscount)),
    );
    setDiscountPercentage(valueTrimmed);
    if (
      !isValidPercentage ||
      newDiscountPercentage <= 0 ||
      !isCalculatedFinite
    ) {
      setDiscount('');
    } else {
      setDiscount(calculatedDiscount.toFixed(2));
    }
  };

  const handleConfirm = () => {
    const discountValueNumber = discountNumber;
    const updatedDiscounts = editedDiscounts.filter(
      (d) => d.id !== 'editCouponDiscount',
    );
    if (discountValueNumber !== undefined) {
      updatedDiscounts.push({
        id: 'editCouponDiscount',
        discountType: 'coupon_discount',
        editType: 'add',
        value: discountValueNumber ?? 0,
        removed: false,
        name: t('editOrders.discountModal.customDiscount'),
        appearance: 'danger',
        prefix: '-',
      });
    }
    updateDiscounts(updatedDiscounts);
    closeModal();
  };

  const handleRemove = () => {
    const updatedDiscounts = editedDiscounts.map((d) => {
      if (d.id === existingCouponDiscount?.id) {
        return {
          ...d,
          editType: undefined,
        };
      }

      if (d.id === 'editCouponDiscount') {
        return {
          ...d,
          editType: 'remove' as OrderEditDiscountType,
        };
      }

      return d;
    });
    setEditedDiscounts(updatedDiscounts);
    if (existingCouponDiscount) {
      setCouponReplaced(false);
    }
    setDiscount('');
    setDiscountPercentage('');
    setShowEditDiscountInput(false);
    setHasError(false);
  };

  const handleCloseModal = () => {
    setDiscount(initialDiscountValue?.toString() ?? '');
    setDiscountPercentage('');
    setHasError(false);
    closeModal();
  };

  const discountsEdited = useMemo(
    () =>
      editedDiscounts.some((ed) => {
        const originalDiscount = discounts.find((d) => d.id === ed.id);
        return ed.editType !== originalDiscount?.editType;
      }),
    [discounts, editedDiscounts],
  );

  const isConfirmDisabled = hasError || (!discountsEdited && discount === '');

  const handleCheckboxChange = (
    checked: boolean,
    discountId: string,
    discountType: string,
  ) => {
    const newEditType = checked ? undefined : 'remove';
    let newEditedDiscounts;
    if (discountId === 'promotions') {
      newEditedDiscounts = editedDiscounts.map((discount) => {
        if (discount.discountType === PROMOTIONAL_DISCOUNT) {
          return {
            ...discount,
            editType: newEditType,
          };
        }
        return discount;
      });
    } else {
      newEditedDiscounts = editedDiscounts.map((d) => {
        if (d.id === discountId && d.discountType === discountType) {
          return {
            ...d,
            editType: newEditType,
          };
        }
        return d;
      });
    }
    setEditedDiscounts(newEditedDiscounts);
    const newDiscountValueNumber = parseNumber(discount);
    const newMaxDiscount =
      getMaxDiscountFromEditedDiscounts(newEditedDiscounts);
    setHasError(
      discount !== '' &&
        showEditDiscountInput &&
        (newDiscountValueNumber <= 0 ||
          newDiscountValueNumber > newMaxDiscount),
    );
  };

  const handleLinkClick = () => {
    setShowEditDiscountInput(true);
    if (existingCouponDiscount) {
      setEditedDiscounts(
        editedDiscounts.map((d) => {
          if (
            d.id === existingCouponDiscount.id &&
            d.discountType === existingCouponDiscount.discountType
          ) {
            return {
              ...d,
              editType: 'remove',
            };
          }
          return d;
        }),
      );
      setCouponReplaced(true);
    }
  };

  const nonPromotionalDiscounts = editedDiscounts.filter(
    (d) =>
      d.discountType !== PROMOTIONAL_DISCOUNT && d.id !== 'editCouponDiscount',
  );
  const promotionalDiscounts = editedDiscounts.filter(
    (d) => d.discountType === PROMOTIONAL_DISCOUNT,
  );
  const promotionsSelected = promotionalDiscounts.some(
    (d) => d.editType !== 'remove',
  );
  const totalPromotionsValue = promotionalDiscounts.reduce(
    (acc, p) => acc + p.value,
    0,
  );

  const getMaxDiscountFromEditedDiscounts = useCallback(
    (modifiedDiscounts: OrderEditDiscount[]) => {
      const editedDiscountsTotal = modifiedDiscounts
        .filter((d) => d.editType !== 'remove' && d.id !== 'editCouponDiscount')
        .reduce((acc, discount) => acc + discount.value, 0);
      return newTotalWithoutDiscounts - editedDiscountsTotal;
    },
    [newTotalWithoutDiscounts],
  );

  const maxDiscount = useMemo(
    () => getMaxDiscountFromEditedDiscounts(editedDiscounts),
    [editedDiscounts, getMaxDiscountFromEditedDiscounts],
  );

  return (
    <Modal open={show} onDismiss={handleCloseModal}>
      <Modal.Header
        title={
          editDiscount
            ? t('editOrders.discountModal.titleEdit')
            : t('editOrders.discountModal.titleAdd')
        }
      />
      <Modal.Body padding="none">
        {couponReplaced && (
          <Box marginBottom="2">
            <Alert appearance="warning">
              {t('editOrders.discountModal.alert', {
                couponName: existingCouponDiscount?.name,
              })}
            </Alert>
          </Box>
        )}
        <Box display="flex" flexDirection="column" gap="4">
          <Text color="neutral-textLow">
            {discounts.length > 0
              ? t('editOrders.discountModal.descriptionPlural')
              : t('editOrders.discountModal.description')}
          </Text>
          {nonPromotionalDiscounts.map((discount) => (
            <DiscountRow
              key={discount.id}
              id={discount.id}
              type={discount.discountType}
              name={discount.name}
              value={discount.value}
              handleCheckboxChange={handleCheckboxChange}
              currency={currency}
              checked={discount.editType !== 'remove'}
              disabled={
                discount.discountType === 'coupon_discount' &&
                showEditDiscountInput
              }
              withCheckbox
            />
          ))}
          {promotionalDiscounts.length > 0 && (
            <DiscountRow
              id="promotions"
              type="promotions"
              name={t('editOrders.discountModal.promotions')}
              value={totalPromotionsValue}
              handleCheckboxChange={handleCheckboxChange}
              currency={currency}
              checked={promotionsSelected}
              withCheckbox
            />
          )}
          {promotionalDiscounts.map((promotion) => (
            <Box paddingLeft="6" key={promotion.id}>
              <Text color="neutral-textHigh">{promotion.name}</Text>
            </Box>
          ))}
          {showEditDiscountInput && (
            <Box>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box paddingTop="2">
                  <Text color="neutral-textHigh">
                    {t('editOrders.discountModal.customDiscount')}
                  </Text>
                </Box>
                <Box display="flex" alignItems="flex-end">
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    gap="1"
                    maxWidth="70px"
                  >
                    <InputPercentageWithError
                      hasError={hasError}
                      handleInputChange={handlePercentageInputChange}
                      discount={discountPercentage}
                      discountNumber={(() => {
                        const parsed = parseNumber(discountPercentage);
                        return Number.isNaN(parsed) ? undefined : parsed;
                      })()}
                      maxDiscount={maxDiscount}
                    />
                  </Box>
                  <Box
                    display="flex"
                    gap="1"
                    paddingLeft="2"
                    alignItems="flex-start"
                    maxWidth="160px"
                  >
                    <InputWithError
                      hasError={hasError}
                      handleInputChange={handleInputChange}
                      discount={discount}
                      discountNumber={discountNumber}
                      maxDiscount={maxDiscount}
                    />
                    {discounts.length > 0 && (
                      <Box width="32px">
                        <IconButton
                          source={<TrashIcon />}
                          onClick={handleRemove}
                          size="2rem"
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
              {hasError && (
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-evenly"
                  alignItems="flex-start"
                  paddingTop="2"
                >
                  <Box flex="1" />
                  <Box display="flex" alignItems="center" gap="1" flex="1">
                    <Icon
                      color="danger-textLow"
                      source={<CheckCircleIcon size="small" />}
                    />
                    <Text
                      as="span"
                      color="danger-textLow"
                      fontSize="caption"
                      lineHeight="caption"
                    >
                      {discountNumber && discountNumber > maxDiscount
                        ? t('editOrders.discountModal.errorMaxCaption')
                        : t('editOrders.discountModal.errorCaption')}
                    </Text>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          <Box
            borderStyle="solid"
            borderTopWidth="1"
            borderBottomWidth="1"
            borderColor="neutral-surfaceDisabled"
            paddingY="2"
          >
            <DiscountRow
              id="totalDiscounts"
              type="totalDiscounts"
              name={t('editOrders.discountModal.totalDiscounts')}
              currency={currency}
              value={totalDiscount}
              handleCheckboxChange={handleCheckboxChange}
              checked
            />
          </Box>
          {!showEditDiscountInput && (
            <Box
              paddingBottom="2"
              paddingLeft="2"
              display="flex"
              flexDirection="row"
              gap="2-5"
            >
              <Link
                appearance="primary"
                textDecoration="none"
                onClick={handleLinkClick}
              >
                <Icon color="primary-interactive" source={<PlusCircleIcon />} />
                {t('editOrders.summary.addDiscount')}
              </Link>
            </Box>
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <CancelAndConfirmButtons
          onCancel={handleCloseModal}
          onConfirm={handleConfirm}
          isConfirmDisabled={isConfirmDisabled}
          confirmText={
            editDiscount
              ? t('editOrders.discountModal.editButton')
              : t('editOrders.discountModal.addButton')
          }
        />
      </Modal.Footer>
    </Modal>
  );
}
